import { Button } from "@mui/material";
import Hls from "hls.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import useVideoProgress from "../hooks/useVideoProgess"
import { useFinishVideoMutation, useUpdateVideoProgressMutation } from "../rtk/videoTutorial";

/**
 * Props:
 *  - src: string            // HLS master.m3u8 (CMAF/fMP4)
 *  - poster?: string
 *  - captions?: { src: string; srclang: string; label: string; default?: boolean }[]
 *  - startMuted?: boolean   // default false
 */
export default function VideoPlayer({ src, poster, captions = [], startMuted = false, videoId = null }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [levels, setLevels] = useState([]); // [{height, bitrate, name, index}]
  const [currentLevel, setCurrentLevel] = useState(-1); // -1 = auto
  const supportsNativeHls = useMemo(
    () => !!document.createElement("video").canPlayType("application/vnd.apple.mpegurl"),
    []
  );

  const [finishVideo, ] = useFinishVideoMutation()
  const [updateProgress, ] = useUpdateVideoProgressMutation()

  const onProgress = useCallback(async (secondsWatchedDelta, currentTime, duration) => {
    if (currentTime > 3) {
      await updateProgress({ video: videoId }).unwrap().catch(() => { });
    }
  }, [videoId]);

  const onComplete = useCallback(async () => {
    if (videoId) {
      await finishVideo({ video: videoId }).unwrap().catch(() => { });
    }
  }, [videoId]);

  useVideoProgress(videoRef, { onProgress, onComplete });

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    // Clean previous instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // Safari: native HLS
    if (supportsNativeHls) {
      video.src = src;
      video.addEventListener("loadedmetadata", onParsed);
      return () => video.removeEventListener("loadedmetadata", onParsed);
    }

    // Other browsers: hls.js
    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        backBufferLength: 60,
        maxBufferLength: 30,
        progressive: true, // good with CMAF

        xhrSetup: (xhr) => {
          xhr.withCredentials = true; // send cookies to the media origin
        },
      });
      hlsRef.current = hls;

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
          else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
          else hls.destroy();
        }
      });

      hls.on(Hls.Events.MANIFEST_PARSED, (_, manifest) => {
        const mapped = hls.levels.map((lvl, idx) => ({
          index: idx,
          height: lvl.height,
          bitrate: lvl.bitrate,
          name: `${lvl.height}p`,
        }));
        setLevels(mapped);
        setCurrentLevel(-1); // auto
        // Autoplay policy friendly
        if (startMuted) {
          video.muted = true;
          video.play().catch(() => { });
        }
      });

      hls.attachMedia(video);
      hls.loadSource(src);

      return () => hls.destroy();
    }

    // Very old browsers: do nothing (could add MP4 fallback)
  }, [src, supportsNativeHls, startMuted]);

  // When user picks a quality
  useEffect(() => {
    const hls = hlsRef.current;
    if (!hls) return;
    hls.currentLevel = currentLevel; // -1 = auto
  }, [currentLevel]);

  // Small helper to show nice labels
  const levelLabel = (lvl) => (lvl.height ? `${lvl.height}p` : "Auto");

  // Keyboard shortcuts
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onKey = (e) => {
      if (document.activeElement && document.activeElement.tagName === "INPUT") return;
      if (e.key === " ") { e.preventDefault(); v.paused ? v.play() : v.pause(); }
      if (e.key === "ArrowRight") v.currentTime += 5;
      if (e.key === "ArrowLeft") v.currentTime -= 5;
      if (e.key === "m" || e.key === "M") v.muted = !v.muted;
      if (e.key === "+" || e.key === "=") v.playbackRate = Math.min(2, (v.playbackRate || 1) + 0.25);
      if (e.key === "-" || e.key === "_") v.playbackRate = Math.max(0.25, (v.playbackRate || 1) - 0.25);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function onParsed() {
    // Native HLS: we don’t get level info easily; rely on built-in ABR.
    if (startMuted) {
      const v = videoRef.current;
      v.muted = true;
      v.play().catch(() => { });
    }
  }

  return (
    <div style={{ width: "100%", maxWidth: 960 }}>
      <video
        ref={videoRef}
        controls
        playsInline
        poster={poster}
        preload="metadata"
        style={{ width: "100%", background: "#000", borderRadius: 12 }}
      >
        {captions.map((c) => (
          <track
            key={c.src}
            kind="subtitles"
            srcLang={c.srclang}
            label={c.label}
            src={c.src}
            default={c.default || false}
          />
        ))}
      </video>

      {/* Quality selector (works in hls.js mode). Safari uses its native UI/ABR. */}
      {!supportsNativeHls && (
        <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
          <Button
            onClick={() => setCurrentLevel(-1)}
            title="Auto"
            variant={currentLevel === -1 ? "contained" : "text"}
            color={currentLevel === -1 ? "primary" : "cancel"}
          >
            Auto
          </Button>
          {levels
            .slice()
            .sort((a, b) => b.height - a.height)
            .map((l) => (
              <Button
                key={l.index}
                onClick={() => setCurrentLevel(l.index)}
                variant={currentLevel === l.index ? "contained" : "text"}
                color={currentLevel === l.index ? "primary" : "cancel"}
                title={`${l.height}p`}
              >
                {`${l.height}p`}
              </Button>
            ))}
        </div>
      )}
    </div>
  );
}

function btnStyle(active) {
  return {
    padding: "6px 10px",
    borderRadius: 8,
    border: active ? "1px solid #333" : "1px solid #bbb",
    background: active ? "#222" : "#fff",
    color: active ? "#fff" : "#222",
    cursor: "pointer",
  };
}
